import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Plus, Power, PowerOff } from "lucide-react";
import type { AdminPromotion } from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, Label, NativeSelect } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { dateTime, minor } from "@/lib/format";
import { parseEuroToMinor, parsePct } from "@/lib/money-input";

interface Option {
  id: string;
  code: string;
  name: string;
}
interface Options {
  markets: (Option & { currency: string })[];
  categories: Option[];
  services: Option[];
}

const STATE: Record<
  AdminPromotion["state"],
  { label: string; variant: "success" | "secondary" | "outline" | "warning" }
> = {
  active: { label: "Yayında", variant: "success" },
  scheduled: { label: "Planlandı", variant: "warning" },
  ended: { label: "Bitti", variant: "secondary" },
  off: { label: "Kapalı", variant: "outline" },
};
const AUDIENCE: Record<AdminPromotion["audience"], string> = {
  all: "Herkes",
  first_order: "İlk sipariş",
  user_list: "Seçili kişiler",
};

const pctText = (bps: number | null) => (bps === null ? "" : String(bps / 100).replace(".", ","));
const euroText = (m: string | null) =>
  m === null ? "" : (Number(m) / 100).toFixed(2).replace(".", ",").replace(",00", "");
/** ISO → datetime-local (tarayıcının saat dilimi; panel Malta'da kullanılır) */
function toLocalInput(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function discountText(
  p: Pick<AdminPromotion, "discountType" | "valueBps" | "valueMinor" | "maxDiscountMinor">,
) {
  if (p.discountType === "percent")
    return `Poji ücretinin %${pctText(p.valueBps)}'i${p.maxDiscountMinor ? ` (en çok ${minor(p.maxDiscountMinor)})` : ""}`;
  return `Poji ücretinden ${minor(p.valueMinor)}`;
}

const init = (p: AdminPromotion | null, opts: Options | undefined) => ({
  marketId: p?.marketId ?? opts?.markets[0]?.id ?? "",
  name: p?.name ?? "",
  code: p?.code ?? "",
  audience: (p?.audience === "first_order" ? "first_order" : "all") as "all" | "first_order",
  discountType: p?.discountType ?? ("percent" as "percent" | "amount"),
  value: p?.discountType === "amount" ? euroText(p.valueMinor) : p ? pctText(p.valueBps) : "",
  max: euroText(p?.maxDiscountMinor ?? null),
  minOrder: euroText(p?.minOrderMinor ?? null),
  scope: (p?.serviceId ? "service" : p?.categoryId ? "category" : "all") as
    "all" | "category" | "service",
  categoryId: p?.categoryId ?? "",
  serviceId: p?.serviceId ?? "",
  startsAt: toLocalInput(p?.startsAt ?? null),
  endsAt: toLocalInput(p?.endsAt ?? null),
  maxRedemptions: p?.maxRedemptions ? String(p.maxRedemptions) : "",
  perUserLimit: p?.perUserLimit ? String(p.perUserLimit) : p ? "" : "1",
});

function PromotionDialog({
  promotion,
  options,
  trigger,
}: {
  promotion: AdminPromotion | null;
  options: Options | undefined;
  trigger: React.ReactNode;
}) {
  const qc = useQueryClient();
  const [f, setF] = React.useState(() => init(promotion, options));
  const set = (p: Partial<ReturnType<typeof init>>) => setF((x) => ({ ...x, ...p }));
  const valueBps = f.discountType === "percent" ? parsePct(f.value) : null;
  const valueMinor = f.discountType === "amount" ? parseEuroToMinor(f.value) : null;
  const maxMinor = f.max.trim() ? parseEuroToMinor(f.max) : null;
  const minOrder = f.minOrder.trim() ? parseEuroToMinor(f.minOrder) : null;
  const int = (v: string) => (v.trim() && /^\d+$/.test(v.trim()) ? Number(v) : null);
  const starts = f.startsAt ? new Date(f.startsAt) : null;
  const ends = f.endsAt ? new Date(f.endsAt) : null;
  const ok =
    f.name.trim().length >= 2 &&
    Boolean(f.marketId) &&
    (f.discountType === "percent"
      ? valueBps !== null && valueBps > 0
      : valueMinor !== null && valueMinor !== "0") &&
    (!f.max.trim() || maxMinor !== null) &&
    (!f.minOrder.trim() || minOrder !== null) &&
    (!f.code.trim() || /^[A-Za-z0-9_-]{3,32}$/.test(f.code.trim())) &&
    (f.scope !== "category" || Boolean(f.categoryId)) &&
    (f.scope !== "service" || Boolean(f.serviceId)) &&
    (!starts || !ends || ends > starts);

  return (
    <ActionDialog
      wide
      trigger={trigger}
      onOpen={() => setF(init(promotion, options))}
      title={promotion ? "Kampanyayı düzenle" : "Yeni kampanya"}
      description="İndirim yalnızca Poji ücretinden düşer; hizmet veren her durumda kendi fiyatının tamamını alır. Kampanya talep anında işe yazılır: sonradan yapılan değişiklik eski işleri etkilemez."
      confirmLabel="Kaydet"
      canSubmit={ok}
      success="Kampanya kaydedildi."
      onSubmit={async (key) => {
        const body = {
          marketId: f.marketId,
          name: f.name.trim(),
          code: f.code.trim() || null,
          audience: f.audience,
          discountType: f.discountType,
          valueBps,
          valueMinor,
          maxDiscountMinor: f.discountType === "percent" ? maxMinor : null,
          minOrderMinor: minOrder,
          categoryId: f.scope === "category" ? f.categoryId : null,
          serviceId: f.scope === "service" ? f.serviceId : null,
          startsAt: starts?.toISOString() ?? null,
          endsAt: ends?.toISOString() ?? null,
          maxRedemptions: int(f.maxRedemptions),
          perUserLimit: int(f.perUserLimit),
        };
        await api(promotion ? `/v1/admin/promotions/${promotion.id}` : "/v1/admin/promotions", {
          method: promotion ? "PUT" : "POST",
          ...(promotion ? {} : { idempotencyKey: key }),
          body,
        });
        await qc.invalidateQueries({ queryKey: ["admin-promotions"] });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2 sm:col-span-2">
          <Label htmlFor="pr-name">Ad (iç)</Label>
          <Input
            id="pr-name"
            maxLength={120}
            placeholder="ör. 1 Kasım tüm gün %50"
            value={f.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        {!promotion && options && options.markets.length > 1 && (
          <div className="grid gap-2">
            <Label htmlFor="pr-market">Pazar</Label>
            <NativeSelect
              id="pr-market"
              value={f.marketId}
              onChange={(e) => set({ marketId: e.target.value })}
            >
              {options.markets.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name ?? m.code}
                </option>
              ))}
            </NativeSelect>
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="pr-audience">Kimler</Label>
          <NativeSelect
            id="pr-audience"
            value={f.audience}
            onChange={(e) => set({ audience: e.target.value as "all" | "first_order" })}
          >
            <option value="all">Herkes</option>
            <option value="first_order">Yalnızca ilk siparişi</option>
          </NativeSelect>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-code">Kod (isteğe bağlı)</Label>
          <Input
            id="pr-code"
            placeholder="Boşsa kendiliğinden uygulanır"
            value={f.code}
            onChange={(e) => set({ code: e.target.value.toUpperCase() })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-type">İndirim</Label>
          <NativeSelect
            id="pr-type"
            value={f.discountType}
            onChange={(e) =>
              set({ discountType: e.target.value as "percent" | "amount", value: "" })
            }
          >
            <option value="percent">Poji ücretinin yüzdesi</option>
            <option value="amount">Poji ücretinden sabit tutar</option>
          </NativeSelect>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-value">{f.discountType === "percent" ? "Yüzde" : "Tutar (€)"}</Label>
          <Input
            id="pr-value"
            inputMode="decimal"
            placeholder={f.discountType === "percent" ? "ör. 50" : "ör. 5"}
            value={f.value}
            aria-invalid={
              Boolean(f.value.trim()) &&
              (f.discountType === "percent" ? valueBps === null : valueMinor === null)
            }
            onChange={(e) => set({ value: e.target.value })}
          />
        </div>
        {f.discountType === "percent" && (
          <div className="grid gap-2">
            <Label htmlFor="pr-max">En çok indirim (€, isteğe bağlı)</Label>
            <Input
              id="pr-max"
              inputMode="decimal"
              value={f.max}
              onChange={(e) => set({ max: e.target.value })}
            />
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="pr-scope">Hangi hizmetler</Label>
          <NativeSelect
            id="pr-scope"
            value={f.scope}
            onChange={(e) => set({ scope: e.target.value as "all" | "category" | "service" })}
          >
            <option value="all">Tümü</option>
            <option value="category">Bir kategori</option>
            <option value="service">Bir hizmet</option>
          </NativeSelect>
        </div>
        {f.scope === "category" && (
          <div className="grid gap-2">
            <Label htmlFor="pr-cat">Kategori</Label>
            <NativeSelect
              id="pr-cat"
              value={f.categoryId}
              onChange={(e) => set({ categoryId: e.target.value })}
            >
              <option value="">Seçin</option>
              {options?.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </div>
        )}
        {f.scope === "service" && (
          <div className="grid gap-2">
            <Label htmlFor="pr-svc">Hizmet</Label>
            <NativeSelect
              id="pr-svc"
              value={f.serviceId}
              onChange={(e) => set({ serviceId: e.target.value })}
            >
              <option value="">Seçin</option>
              {options?.services.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="pr-start">Başlangıç (Malta saati)</Label>
          <Input
            id="pr-start"
            type="datetime-local"
            value={f.startsAt}
            onChange={(e) => set({ startsAt: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-end">Bitiş (Malta saati)</Label>
          <Input
            id="pr-end"
            type="datetime-local"
            value={f.endsAt}
            aria-invalid={Boolean(starts && ends && ends <= starts)}
            onChange={(e) => set({ endsAt: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-min">En az sipariş (€, isteğe bağlı)</Label>
          <Input
            id="pr-min"
            inputMode="decimal"
            value={f.minOrder}
            onChange={(e) => set({ minOrder: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-maxr">Toplam kullanım sınırı</Label>
          <Input
            id="pr-maxr"
            inputMode="numeric"
            placeholder="Sınırsız"
            value={f.maxRedemptions}
            onChange={(e) => set({ maxRedemptions: e.target.value })}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="pr-per">Kişi başı kullanım</Label>
          <Input
            id="pr-per"
            inputMode="numeric"
            placeholder="Sınırsız"
            value={f.perUserLimit}
            onChange={(e) => set({ perUserLimit: e.target.value })}
          />
        </div>
        <p className="text-xs text-muted-foreground sm:col-span-2">
          Talep edildiği güne göre uygulanır. Aynı anda birden çok kampanya uyarsa müşteriye en çok
          indirim yapan uygulanır; kodlu kampanya yalnızca kod girilince geçerlidir. Kampanyalar
          birleşmez.
        </p>
      </div>
    </ActionDialog>
  );
}

function Toggle({ p }: { p: AdminPromotion }) {
  const qc = useQueryClient();
  const on = p.isActive;
  return (
    <ActionDialog
      trigger={
        <Button variant="ghost" size="sm">
          {on ? <PowerOff /> : <Power />} {on ? "Kapat" : "Aç"}
        </Button>
      }
      title={on ? "Kampanyayı kapat" : "Kampanyayı aç"}
      description={
        on
          ? "Yeni taleplerde uygulanmaz. Daha önce talep edilmiş işler indirimini korur."
          : "Tarih aralığı içindeyse yeni taleplerde uygulanır."
      }
      confirmLabel={on ? "Kampanyayı kapat" : "Kampanyayı aç"}
      success={on ? "Kampanya kapatıldı." : "Kampanya açıldı."}
      onSubmit={async (key) => {
        await api(`/v1/admin/promotions/${p.id}/${on ? "deactivate" : "activate"}`, {
          method: "POST",
          idempotencyKey: key,
          body: {},
        });
        await qc.invalidateQueries({ queryKey: ["admin-promotions"] });
      }}
    />
  );
}

export function PromotionsPage() {
  const q = useQuery({
    queryKey: ["admin-promotions"],
    queryFn: () => api<{ promotions: AdminPromotion[] }>("/v1/admin/promotions"),
  });
  const opts = useQuery({
    queryKey: ["admin-promotions-options"],
    queryFn: () => api<Options>("/v1/admin/promotions/options"),
  });
  const scope = (p: AdminPromotion) =>
    p.serviceId
      ? (opts.data?.services.find((s) => s.id === p.serviceId)?.name ?? "Hizmet")
      : p.categoryId
        ? (opts.data?.categories.find((c) => c.id === p.categoryId)?.name ?? "Kategori")
        : "Tüm hizmetler";
  const columns: ColumnDef<AdminPromotion, unknown>[] = [
    {
      id: "name",
      header: "Kampanya",
      cell: ({ row }) => (
        <div className="grid gap-0.5">
          <span className="font-medium">{row.original.name}</span>
          <span className="text-xs text-muted-foreground">
            {row.original.code ? (
              <span className="font-mono">{row.original.code}</span>
            ) : (
              "Kodsuz (otomatik)"
            )}{" "}
            · {AUDIENCE[row.original.audience]} · {scope(row.original)}
          </span>
        </div>
      ),
    },
    { id: "discount", header: "İndirim", cell: ({ row }) => discountText(row.original) },
    {
      id: "when",
      header: "Tarih",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.startsAt ? dateTime(row.original.startsAt) : "Hemen"} →{" "}
          {row.original.endsAt ? dateTime(row.original.endsAt) : "süresiz"}
        </span>
      ),
    },
    {
      id: "usage",
      header: "Kullanım",
      cell: ({ row }) => (
        <span className="tabular text-sm">
          {row.original.redemptions}
          {row.original.maxRedemptions ? ` / ${row.original.maxRedemptions}` : ""} ·{" "}
          {minor(row.original.discountTotalMinor)}
        </span>
      ),
    },
    {
      id: "state",
      header: "Durum",
      cell: ({ row }) => (
        <Badge variant={STATE[row.original.state].variant}>{STATE[row.original.state].label}</Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <PromotionDialog
            promotion={row.original}
            options={opts.data}
            trigger={
              <Button variant="ghost" size="sm" aria-label="Düzenle">
                <Pencil />
              </Button>
            }
          />
          <Toggle p={row.original} />
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Kampanyalar"
        description="Süreli, ilk siparişe özel, kodlu ya da kodsuz indirimler (K-016). İndirimi Poji karşılar: yalnızca Poji ücretinden düşer, hizmet veren tam alır."
        actions={
          <PromotionDialog
            promotion={null}
            options={opts.data}
            trigger={
              <Button size="sm" disabled={!opts.data}>
                <Plus /> Yeni kampanya
              </Button>
            }
          />
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.promotions}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Henüz kampanya yok."
      />
    </>
  );
}
